import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/cn';

/**
 * FormField — Label + (anything) + optional error/hint, with proper a11y
 * wiring (aria-describedby, aria-invalid). Eliminates the repetitive
 *
 *   <div className="space-y-1.5">
 *     <Label htmlFor="…">…</Label>
 *     <Input id="…" {...} />
 *     {error ? <p className="text-xs text-destructive">{error.message}</p> : null}
 *   </div>
 *
 * pattern. Pass children directly (Input/Select/textarea) — FormField only
 * owns the wrapper, label, hint and error rendering.
 */
export interface FormFieldProps {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  /** When true, label is visually hidden (still announced by SR). */
  hideLabel?: boolean;
  className?: string;
  children: ReactNode;
}

export function FormField({
  label,
  htmlFor,
  hint,
  error,
  hideLabel = false,
  className,
  children,
}: FormFieldProps): JSX.Element {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label
        htmlFor={htmlFor}
        className={cn(hideLabel && 'sr-only')}
      >
        {label}
      </Label>
      {children}
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * TextField — `<FormField>` + `<Input>` combo for the common case.
 */
export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  id?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, hint, error, id, ...inputProps }, ref) => {
    const auto = useId();
    const inputId = id ?? auto;
    return (
      <FormField label={label} htmlFor={inputId} hint={hint} error={error}>
        <Input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          {...inputProps}
        />
      </FormField>
    );
  },
);
TextField.displayName = 'TextField';

/**
 * TextAreaField — same, for multi-line input.
 */
export interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  id?: string;
  /** When true, label is visually hidden (still announced by SR). */
  hideLabel?: boolean;
}

/**
 * PasswordField — `<TextField>` for passwords with a baked-in eye toggle
 * (right-aligned inside the input). Identical API to `TextField` minus the
 * `type` prop, which is managed internally.
 */
export interface PasswordFieldProps
  extends Omit<TextFieldProps, 'type'> {
  /** Override the show/hide button accessible labels. */
  showLabel?: string;
  hideLabel?: string;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  (
    {
      label,
      hint,
      error,
      id,
      className,
      showLabel = 'Show password',
      hideLabel = 'Hide password',
      ...inputProps
    },
    ref,
  ) => {
    const auto = useId();
    const inputId = id ?? auto;
    const [shown, setShown] = useState(false);
    return (
      <FormField label={label} htmlFor={inputId} hint={hint} error={error}>
        <div className="relative">
          <Input
            ref={ref}
            id={inputId}
            type={shown ? 'text' : 'password'}
            aria-invalid={error ? true : undefined}
            // Leave room for the toggle button on the right.
            className={cn('pr-10', className)}
            {...inputProps}
          />
          <button
            type="button"
            onClick={() => setShown((v) => !v)}
            aria-label={shown ? hideLabel : showLabel}
            aria-pressed={shown}
            tabIndex={-1}
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {shown ? (
              <EyeOff className="h-4 w-4" aria-hidden />
            ) : (
              <Eye className="h-4 w-4" aria-hidden />
            )}
          </button>
        </div>
      </FormField>
    );
  },
);
PasswordField.displayName = 'PasswordField';

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  ({ label, hint, error, id, className, rows = 3, hideLabel = false, ...rest }, ref) => {
    const auto = useId();
    const inputId = id ?? auto;
    return (
      <FormField
        label={label}
        htmlFor={inputId}
        hint={hint}
        error={error}
        hideLabel={hideLabel}
      >
        <textarea
          ref={ref}
          id={inputId}
          rows={rows}
          aria-invalid={error ? true : undefined}
          className={cn(
            'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          {...rest}
        />
      </FormField>
    );
  },
);
TextAreaField.displayName = 'TextAreaField';
